import { loadWrapper } from "aic";
import { AppSettingsContext } from "contexts/AppSettingsContext";
import { HierarchyContext } from "contexts/HierarchyContext";
import { IRequestStatus } from "functions/api/interface";
import { useContext, useEffect, useEffectEvent, useState } from "react";
import { getReportSuffixes } from "../../functions/api/hierarchy/getReportSuffixes";
import { getReportTypes } from "../../functions/api/hierarchy/getReportTypes";
import { WordModal } from "../common/modals/WordModal";
import { newReportInputs } from "./newReportInputs";

export interface NewReportModalProps {
  show: boolean;
  onOk: (data: { [key: string]: string }) => void;
  onCancel: () => void;
}

/**
 * Modal for creating a new report with structured naming fields.
 * Provides dropdowns for report type and suffix, with computed name field.
 *
 * @param props - The component props
 * @param props.show - Whether the modal is currently displayed
 * @param props.onOk - Callback invoked with field values when OK is clicked
 * @param props.onCancel - Callback invoked when modal is cancelled
 */
export const NewReportModal = ({ show, onOk, onCancel }: NewReportModalProps) => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const hierarchy = hierarchyContext?.hierarchy;
  const [reportTypes, setReportTypes] = useState<string[]>([]);
  const [reportSuffixes, setReportSuffixes] = useState<string[]>([]);

  const [requestTypes, setRequestTypes] = useState<IRequestStatus<null>>({
    requesting: false,
    error: false,
  });
  const getReportTypesData = useEffectEvent(async () => {
    const response = await loadWrapper(
      getReportTypes,
      null,
      requestTypes,
      setRequestTypes,
      appSettings.backEndUrl,
    );
    if (response?.success) {
      setReportTypes(response.data!);
    }
  });
  const [requestSuffixes, setRequestSuffixes] = useState<IRequestStatus<null>>({
    requesting: false,
    error: false,
  });
  const getReportSuffixesData = useEffectEvent(async () => {
    const response = await loadWrapper(
      getReportSuffixes,
      null,
      requestSuffixes,
      setRequestSuffixes,
      appSettings.backEndUrl,
    );
    if (response?.success) {
      setReportSuffixes(response.data!);
    }
  });

  useEffect(() => {
    getReportTypesData();
    getReportSuffixesData();
  }, []);

  return !hierarchy ? null : (
    <WordModal
      id="hierarchylinks-add-modal"
      show={show}
      title="Add new Report"
      buttonLabel="Add"
      onOk={onOk}
      onCancel={onCancel}
      message={
        requestTypes.error || requestSuffixes.error ? (
          <span className="text-danger">Error loading report types or suffixes</span>
        ) : undefined
      }
      inputs={newReportInputs(
        hierarchy.children.map((r) => r.name.toLowerCase()),
        reportTypes,
        reportSuffixes,
      )}
      spellCheck={false}
    />
  );
};

NewReportModal.displayName = "NewReportModal";
